/**
 * How settings records store a makes assignment: the named makes, in the same
 * `{ _id, title }` shape locations use. Re-quote rules and anything else that
 * scopes a rule to a set of makes share these conversions, so the pickers can
 * work in plain id lists.
 */
export type MakeList = Array<{ _id: string; title: string }>;

export interface MakeOption {
    _id: string;
    name: string;
}

export const toMakeIds = (value: MakeList): Array<string> => value.map((make) => make._id);

/**
 * Back to the stored shape. Ids the org no longer has are dropped, since the
 * saved value carries titles that only the make list knows.
 */
export const toMakeList = (value: Array<string>, options: MakeOption[]): MakeList =>
    value
        .map((id) => options.find((option) => option._id === id))
        .filter((option): option is MakeOption => !!option)
        .map((option) => ({ _id: option._id, title: option.name }));

export const isSameMakeList = (a: MakeList, b: MakeList): boolean => {
    if (a.length !== b.length) return false;
    const other = new Set(b.map((make) => make._id));
    return a.every((make) => other.has(make._id));
};

/** "All Makes", the one make's name, or a count. */
export const formatMakeList = (value: MakeList, totalMakes?: number): string => {
    if (value.length === 0) return 'None';
    if (totalMakes && totalMakes > 0 && value.length >= totalMakes) return 'All Makes';
    if (value.length === 1) return value[0].title;
    return `${value.length} makes`;
};
